import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FolderOpen, Users, Truck, ClipboardList, Tags, Award, Ruler, Building2,
  Package, Receipt, FileText, ArrowRightLeft, ShoppingCart, Inbox, PhoneCall,
  Settings, Palette, Library, Wallet, TrendingDown, TrendingUp, Clock, UserCheck, PackagePlus,
  LogOut, Menu as MenuIcon, X, HelpCircle, FileCheck2, MessageSquare, Plug, Megaphone, Globe, Images,
  Sparkles, ShieldCheck, Sun, Moon, Coins, Laptop, Smartphone, MapPin, Boxes, AlertTriangle, Landmark, PackageSearch, Replace, ClipboardCheck,
  ScrollText, Handshake, Percent, GraduationCap, PlayCircle, BookOpen, Compass, Route, PackageX, ScanSearch,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme-context';
import { telasNavegaveis, type TelaNavegavel } from '../../lib/telas-navegaveis';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { DropdownMenu, type MenuItem } from '../DropdownMenu';
import { MockupCaptureOverlay } from '../MockupCaptureOverlay';
import { AlertaSaudeBancoBanner } from '../AlertaSaudeBancoBanner';
import { AlertaContasPagarBanner } from '../AlertaContasPagarBanner';
import { AlertaBoletosVencidosBanner } from '../AlertaBoletosVencidosBanner';
import { AlertaCaixaDivergenciaBanner } from '../AlertaCaixaDivergenciaBanner';
import { AlertaMovimentoCaixaBanner } from '../AlertaMovimentoCaixaBanner';
import { AlertaTransferenciasPendentesBanner } from '../AlertaTransferenciasPendentesBanner';
import { AlertaTrocasPendentesBanner } from '../AlertaTrocasPendentesBanner';
import { AlertaNotasComProblemasBanner } from '../AlertaNotasComProblemasBanner';
import { AlertaEanInvalidoBanner } from '../AlertaEanInvalidoBanner';
import { AlertaNotasDuplicadasBanner } from '../AlertaNotasDuplicadasBanner';
import { AlertaBuracosNumeracaoBanner } from '../AlertaBuracosNumeracaoBanner';
import { AlertaPerdidosBanner } from '../AlertaPerdidosBanner';
import { AlertaParadosBanner } from '../AlertaParadosBanner';
import { AlertaChamadosEsquecidosBanner } from '../AlertaChamadosEsquecidosBanner';
import { AlertaPontoAbertoBanner } from '../AlertaPontoAbertoBanner';
import { AlertaConciliacaoBanner } from '../AlertaConciliacaoBanner';
import { AlertaContaNegativaBanner } from '../AlertaContaNegativaBanner';
import { AlertaEntregaWhatsAppBanner } from '../AlertaEntregaWhatsAppBanner';
import { AlertaInconsistenciasEstoqueBanner } from '../AlertaInconsistenciasEstoqueBanner';
import { AlertaReposicaoBanner } from '../AlertaReposicaoBanner';
import { AlertaDivergenciaRecebimentoBanner } from '../AlertaDivergenciaRecebimentoBanner';
import { AlertaRetomarOrcamentosBanner } from '../AlertaRetomarOrcamentosBanner';
import { AlertasProvider } from '../alertas/AlertasContexto';
import { BarraAlertasResumo, RodapeRecolherAlertas } from '../alertas/BarraAlertasResumo';
import { ModalChamadoParado } from '../ModalChamadoParado';
import { useManifesto, MANIFESTO_SISTEMA } from '../../lib/useManifesto';
import { AdminAtendimentoMenu } from '../../modules/atendimento/AdminAtendimentoMenu';
import { ModalWhatsApp } from '../../modules/atendimento/modais/ModalWhatsApp';
import { ModalAgentes } from '../../modules/atendimento/modais/ModalAgentes';
import { ModalHorarios } from '../../modules/atendimento/modais/ModalHorarios';
import { ModalEtiquetas } from '../../modules/atendimento/modais/ModalEtiquetas';
import { ModalChatAtendimento } from '../../modules/atendimento/modais/ModalChatAtendimento';
import { ModalCores } from '../../modules/configuracoes/Cores';
import { AtalhosRapidosMenu } from '../AtalhosRapidosMenu';
import { RodapeVersao } from '../RodapeVersao';
import { IdentificadorComponentes } from '../IdentificadorComponentes';
import { EmergenciaTarja, EnvioDaEmergencia, TelaIndisponivelNaEmergencia } from '../EmergenciaTarja';
import { telaLiberadaNaEmergencia, useEmergencia } from '../../lib/emergencia';

type ItemMobile = TelaNavegavel;

export function Layout({ children }: { children: ReactNode }) {
  const { usuario, logout } = useAuth();
  const { modo, alternarModo } = useTheme();
  const navigate = useNavigate();
  useDocumentTitle();
  // Dentro do sistema, "Instalar app" instala o app do SISTEMA (start_url /), nao a vitrine.
  useManifesto(MANIFESTO_SISTEMA);
  const [menuMobile, setMenuMobile] = useState(false);
  const [capturaAtiva, setCapturaAtiva] = useState(false);
  const [adminModal, setAdminModal] = useState<'whatsapp' | 'agentes' | 'horarios' | 'etiquetas' | 'chat-atendimento' | null>(
    // O WhatsApp e' o unico destes que se usa recarregando a pagina: pareia o celular, da F5 pra
    // ver se subiu. Estado em `useState` morre no reload, entao ele (e so ele) mora na URL —
    // `?admin=whatsapp` volta com o modal aberto. Os outros seguem efemeros de proposito.
    () => (new URLSearchParams(window.location.search).get('admin') === 'whatsapp' ? 'whatsapp' : null),
  );
  const [modalCores, setModalCores] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Espelha o modal do WhatsApp na URL. `replace` pra nao encher o historico: abrir e fechar o
  // modal nao e' navegacao, e o Voltar do navegador tem que sair da PAGINA.
  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (adminModal === 'whatsapp') {
      if (p.get('admin') === 'whatsapp') return;
      p.set('admin', 'whatsapp');
    } else {
      if (!p.has('admin')) return;
      p.delete('admin');
    }
    setSearchParams(p, { replace: true });
  }, [adminModal, searchParams, setSearchParams]);

  // ⛔ TROCAR DE PAGINA FECHA O MODAL DE ADMIN. Sem isto, o `adminModal` vive no Layout, que
  // NAO remonta ao navegar: clicar em "Contas a Receber" com o WhatsApp aberto trocava a pagina
  // por baixo, o efeito acima recarimbava `?admin=whatsapp` na URL nova, e o modal `telaCheia`
  // seguia cobrindo tudo. A pagina abria de verdade (o log de prod mostrou o fetch dela) e a
  // tela nao mudava um pixel — que e' o pior modo de falhar: se le como "o sistema travou".
  // ⚠️ O `ref` guarda a rota da montagem pra NAO disparar no primeiro render: disparar ali
  // mataria o `?admin=whatsapp` da URL, que existe justamente pra sobreviver ao F5 depois de
  // parear o celular.
  const location = useLocation();
  const rotaAnterior = useRef(location.pathname);

  // MODO EMERGENCIA: navegacao NOVA pra tela que precisa do servidor mostra o aviso no lugar dela.
  // A tela que JA estava aberta quando o servidor caiu fica — arranca-la perderia o que a pessoa
  // digitava (ex.: um orcamento em edicao); as chamadas dela falham com o aviso de emergencia.
  // Estado derivado no PROPRIO render (nao em efeito): com efeito, o 1o render da queda ja
  // trocaria a tela pelo aviso antes de a tela aberta ser lembrada. So muda na virada do modo —
  // navegar depois da queda nao muda "a tela que ja estava aberta".
  const emergencia = useEmergencia();
  const [emergenciaAnterior, setEmergenciaAnterior] = useState(emergencia);
  const [telaNaQueda, setTelaNaQueda] = useState<string | null>(null);
  if (emergencia !== emergenciaAnterior) {
    setEmergenciaAnterior(emergencia);
    setTelaNaQueda(emergencia ? location.pathname : null);
  }
  useEffect(() => {
    if (rotaAnterior.current === location.pathname) return;
    rotaAnterior.current = location.pathname;
    setAdminModal(null);
  }, [location.pathname]);

  // Atalho Alt+T: ativa captura de tela pro mockup-builder. So pra admin.
  useEffect(() => {
    if (usuario?.perfil !== 'admin') return;
    function onKey(e: KeyboardEvent) {
      // Alt+T sem Ctrl/Shift/Meta
      if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.code === 'KeyT') {
        e.preventDefault();
        setCapturaAtiva((v) => !v);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [usuario]);

  const cadastrosAuxiliares: MenuItem[] = [
    { to: '/cadastros/grupos', label: 'Grupos', icon: FolderOpen },
    { to: '/cadastros/categorias', label: 'Categorias', icon: Tags },
    // Metas de Margem (migration 0300): a rota GET cobra admin/gerente no servidor; so admin grava.
    ...(usuario?.perfil === 'admin' || usuario?.perfil === 'gerente'
      ? [{ to: '/cadastros/metas-margem', label: 'Metas de Margem', icon: Percent }] : []),
    { to: '/cadastros/marcas', label: 'Marcas', icon: Award },
    { to: '/cadastros/categorias-despesa', label: 'Cat. de Despesa', icon: Coins },
    { to: '/cadastros/origens-cliente', label: 'Origem do Cliente', icon: Compass },
    { to: '/cadastros/unidades-medida', label: 'Unidades', icon: Ruler },
    { to: '/cadastros/cidades', label: 'Cidades', icon: Building2 },
    { to: '/cadastros/localizacoes', label: 'Localizacoes', icon: MapPin },
    { to: '/configuracoes/situacoes-tributarias', label: 'Sit. Tributaria', icon: Receipt },
    { to: '/cadastros/usuarios', label: 'Usuarios', icon: Users },
  ];

  const arquivos: MenuItem[] = [
    { to: '/produtos', label: 'Produtos', icon: Package },
    { to: '/cadastros/clientes', label: 'Clientes', icon: Users },
    { to: '/cadastros/fornecedores', label: 'Fornecedores', icon: Truck },
    { label: 'Cadastros Auxiliares', icon: ClipboardList, children: cadastrosAuxiliares },
  ];

  const cotacoesSub: MenuItem[] = [
    { to: '/cotacoes', label: 'Principal', icon: ShoppingCart },
    { to: '/catalogo-fornecedor', label: 'Catalogo', icon: Library },
    // A prateleira do item que ainda nao e de ninguem (DA-606). Mora aqui, ao lado do Catalogo,
    // porque e dali que ele sai: a primeira compra o move pro Catalogo do fornecedor da nota.
    { to: '/novos-produtos', label: 'Novos Produtos', icon: PackagePlus },
    { to: '/monitor-precos', label: 'Monitor de Custos', icon: TrendingUp },
    { to: '/rastreio', label: 'Rastreio', icon: Route },
    { to: '/cotacoes/mais-vendidos', label: 'Mais Vendidos', icon: TrendingUp },
  ];

  // Estoque nasceu com uma tela so, e de consulta — o que ficava estranho em Lancamentos, que e onde
  // as coisas acontecem. Decisao consciente do Ruan (2026-07-17): a Conferencia e o relatorio de
  // divergencia entrariam aqui em cima e ai o modulo se bastaria. ✅ A Conferencia chegou em
  // 2026-08-20 ([DA-597]) e vai em PRIMEIRO na lista: e' a unica rotina DIARIA do estoquista, e
  // ordem de menu e' onde a gente diz o que se faz todo dia.
  const estoqueSub: MenuItem[] = [
    { to: '/estoque/conferencia', label: 'Conferencia dos Vendidos', icon: ClipboardCheck },
    { to: '/estoque/localizacoes', label: 'Localizacoes por Produtos', icon: MapPin },
    { to: '/estoque/prateleiras', label: 'Estoque por Prateleira', icon: Boxes },
    { to: '/estoque/reposicoes', label: 'Reposicoes', icon: PackageSearch },
    { to: '/estoque/transferir', label: 'Transferir Estoque', icon: Replace },
    { to: '/estoque/inconsistencias', label: 'Inconsistencias', icon: AlertTriangle },
    // Premio do Estoquista (DA-868): so admin/gerente — o estoquista nao pode ver o sorteio, senao
    // conta primeiro os sorteados e a nota mede a lista, nao o estoque.
    ...(usuario?.perfil === 'admin' || usuario?.perfil === 'gerente'
      ? [{ to: '/estoque/auditoria', label: 'Auditoria do Estoque', icon: ScanSearch }] : []),
  ];

  const lancamentos: MenuItem[] = [
    { to: '/orcamentos', label: 'Orcamentos', icon: FileText },
    { to: '/caixa', label: 'Caixa', icon: ShoppingCart },
    { to: '/trocas', label: 'Trocas e Devolucoes', icon: ArrowRightLeft },
    { label: 'Cotacoes', icon: ShoppingCart, children: cotacoesSub },
    { label: 'Estoque', icon: Boxes, children: estoqueSub },
    { to: '/lista-compras', label: 'Lista de Compras', icon: ClipboardList },
    { to: '/notas-entrada', label: 'Notas de Entrada', icon: Inbox },
    { to: '/notas-fiscais', label: 'Notas Fiscais', icon: FileCheck2 },
    // Ultimo item de Lancamentos, pedido dele (DA-868). Todo perfil registra.
    { to: '/vendas-perdidas', label: 'Venda Perdida', icon: PackageX },
  ];

  const financeiro: MenuItem[] = [
    { to: '/financeiro', label: 'Painel', icon: Landmark },
    { to: '/contas-receber', label: 'Contas a Receber', icon: Wallet },
    { to: '/contas-pagar', label: 'Contas a Pagar', icon: TrendingDown },
    { to: '/consignacao', label: 'Consignacao', icon: Receipt },
  ];

  const marketing: MenuItem[] = [
    { to: '/catalogo-gestao', label: 'Catalogo Digital', icon: Globe },
    { to: '/promocoes', label: 'Promocoes', icon: Megaphone },
    { to: '/gestao-fotos', label: 'Gestao de Fotos', icon: Images },
  ];

  // Modulo Manual (pedido do Ruan, 29/09/2026): o treinamento de quem chega fica a vista na barra,
  // nao escondido atras do "?". O "?" do cabecalho continua, como atalho pra ca.
  const manual: MenuItem[] = [
    { to: '/treinamento', label: 'Videos de Treinamento', icon: PlayCircle },
    { to: '/manual', label: 'Manual Escrito', icon: BookOpen },
  ];

  const podeConfigurar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const equipe: MenuItem[] = [
    { to: '/ponto/registrar', label: 'Bater Ponto', icon: Clock },
    // Veio de Lancamentos (pedido do Ruan, 2026-09-04): a fila de retomada nao e um lancamento,
    // e o acompanhamento do que cada vendedora deixou parado — mora com quem cobra a equipe.
    { to: '/orcamentos/fila', label: 'Gestao de Vendas', icon: PhoneCall },
    { to: '/equipe', label: 'Visualizar Equipe', icon: UserCheck },
    { to: '/comissoes', label: 'Comissoes', icon: TrendingUp },
    ...(podeConfigurar ? [{ to: '/equipe/premio-estoque', label: 'Premio do Estoquista', icon: Award }] : []),
    { to: '/equipe/estatisticas-agentes', label: 'Estatisticas dos agentes', icon: TrendingUp },
    // Sem gate de perfil de proposito: cada um ve o proprio dia, admin ve todos (o servidor forca).
    { to: '/equipe/diario', label: 'Diario', icon: ScrollText },
  ];

  // Programas da loja (DA-445). Nasce com um filho so — mesmo caso do Estoque, e pela mesma razao:
  // a fila de irmaos ja existe (Indicacao, Consignacao) e mudar o menu de lugar depois custa mais
  // do que a estranheza de agora. So-admin, como os parametros que a tela grava.
  const programasLoja: MenuItem[] = [
    { to: '/configuracoes/programas/parceiro', label: 'Parceiro FIODECOBRE', icon: Handshake },
  ];

  const configuracoes: MenuItem[] = [
    { onClick: () => setModalCores(true), label: 'Cores', icon: Palette },
    { to: '/configuracoes/rds', label: 'RDS - Regras do Sistema', icon: ShieldCheck },
    ...(usuario?.perfil === 'admin' ? [{ to: '/configuracoes/dispositivos', label: 'Dispositivos Autorizados', icon: Laptop }] : []),
    // So-admin, como a rota `/api/app-usuarios` ja cobra no servidor.
    ...(usuario?.perfil === 'admin' ? [{ to: '/configuracoes/appcel', label: 'AppCel', icon: Smartphone }] : []),
    ...(usuario?.perfil === 'admin' ? [{ label: 'Programas da Loja', icon: Handshake, children: programasLoja }] : []),
    { to: '/configuracoes/fiscal', label: 'Fiscal', icon: Receipt },
    { to: '/configuracoes/recebimentos', label: 'Recebimentos', icon: Wallet },
    // Um item so: os celulares da loja moram todos na mesma tela (DA-510). Eram
    // dois itens soltos ("Conexao Baileys (Compras)" e "(Atendimento)") e ja nao
    // davam conta — a loja pode ter mais de um aparelho por caixa.
    { onClick: () => setAdminModal('whatsapp'), label: 'WhatsApp', icon: Plug },
    { onClick: () => setAdminModal('agentes'), label: 'Agentes', icon: Users },
    { onClick: () => setAdminModal('horarios'), label: 'Horarios', icon: Clock },
    { onClick: () => setAdminModal('etiquetas'), label: 'Etiquetas', icon: Tags },
    { onClick: () => setAdminModal('chat-atendimento'), label: 'Chat Atendimento', icon: MessageSquare },
    { to: '/configuracoes/catalogo', label: 'Catalogo de Componentes', icon: Library },
  ];

  const itensMobile: ItemMobile[] = telasNavegaveis(podeConfigurar);

  return (
    <div className="app-root">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="header-esq">
            <AtalhosRapidosMenu />
            <button
              type="button"
              className="btn-menu-mobile"
              onClick={() => setMenuMobile(!menuMobile)}
              aria-label="Menu"
            >
              {menuMobile ? <X size={22} /> : <MenuIcon size={22} />}
            </button>
            <h1 className="header-logo" onClick={() => navigate('/')}>FIODECOBRE</h1>
            <nav className="header-nav">
              <DropdownMenu label="Arquivos" icon={FolderOpen} items={arquivos} iconSize={24} />
              <DropdownMenu label="Lancamentos" icon={ArrowRightLeft} items={lancamentos} iconSize={24} />
              <DropdownMenu label="Financeiro" icon={Landmark} items={financeiro} iconSize={24} />
              <DropdownMenu label="Equipe" icon={UserCheck} items={equipe} iconSize={24} />
              <DropdownMenu label="Marketing" icon={Sparkles} items={marketing} iconSize={24} />
              <DropdownMenu label="Manual" icon={GraduationCap} items={manual} iconSize={24} />
            </nav>
          </div>

          <div className="header-dir">
            <AdminAtendimentoMenu />
            {podeConfigurar && (
              <DropdownMenu
                label="Configuracoes"
                icon={Settings}
                items={configuracoes}
                iconSize={24}
                iconOnly
                alignRight
              />
            )}
            <button
              type="button"
              onClick={alternarModo}
              className="btn-icon-header"
              title={modo === 'escuro' ? 'Modo claro' : 'Modo escuro'}
              aria-label={modo === 'escuro' ? 'Mudar para modo claro' : 'Mudar para modo escuro'}
            >
              {modo === 'escuro' ? <Sun size={24} /> : <Moon size={24} />}
            </button>
            <button
              type="button"
              onClick={() => navigate('/treinamento')}
              className="btn-icon-header"
              title="Manual (videos de treinamento e manual escrito)"
              aria-label="Manual do usuario"
            >
              <HelpCircle size={24} />
            </button>
            <span className="usuario-nome">
              {usuario?.nome} <span className="usuario-perfil">({usuario?.perfil})</span>
            </span>
            <button
              type="button"
              onClick={logout}
              className="btn-icon-header"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut size={24} />
            </button>
          </div>
        </div>

        {menuMobile && (
          <nav className="menu-mobile">
            {itensMobile.map((it, idx) => {
              const grupoAnterior = idx > 0 ? itensMobile[idx - 1]!.grupo : '';
              const mostrarDivisor = it.grupo && it.grupo !== grupoAnterior;
              return (
                <div key={it.to}>
                  {mostrarDivisor && <div className="menu-mobile-grupo">{it.grupo}</div>}
                  {emergencia && !telaLiberadaNaEmergencia(it.to) ? (
                    <span className="menu-mobile-item" style={{ opacity: 0.4 }} title="Indisponivel no modo emergencia">
                      <it.icon size={18} />
                      {it.label}
                    </span>
                  ) : (
                  <NavLink
                    to={it.to}
                    onClick={() => setMenuMobile(false)}
                    className={({ isActive }) => `menu-mobile-item ${isActive ? 'ativo' : ''}`}
                  >
                    <it.icon size={18} />
                    {it.label}
                  </NavLink>
                  )}
                </div>
              );
            })}
            {/* Sair no menu mobile: na barra do topo o botao de logout fica em `.header-dir`, que
                transborda pra fora em telas estreitas (celular do balcao nao alcancava). Aqui fica
                sempre acessivel. */}
            <div className="menu-mobile-grupo">Conta</div>
            <button
              type="button"
              onClick={() => { setMenuMobile(false); logout(); }}
              className="menu-mobile-item"
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', font: 'inherit' }}
            >
              <LogOut size={18} />
              Sair ({usuario?.login})
            </button>
          </nav>
        )}
      </header>

      {/* Faixa unica: com 2+ alertas no ar a barra resume tudo numa linha e as faixas abaixo se
          escondem ate alguem clicar. A ordem aqui e a ordem canonica das pastilhas (ORDEM_ALERTAS). */}
      <AlertasProvider>
        <BarraAlertasResumo />
        <AlertaSaudeBancoBanner />
        <AlertaNotasDuplicadasBanner />
        <AlertaNotasComProblemasBanner />
        <AlertaBuracosNumeracaoBanner />
        <AlertaContasPagarBanner />
        <AlertaBoletosVencidosBanner />
        <AlertaContaNegativaBanner />
        <AlertaCaixaDivergenciaBanner />
        <AlertaMovimentoCaixaBanner />
        <AlertaTransferenciasPendentesBanner />
        <AlertaTrocasPendentesBanner />
        <AlertaEntregaWhatsAppBanner />
        <AlertaParadosBanner />
        <AlertaPerdidosBanner />
        <AlertaChamadosEsquecidosBanner />
        <AlertaPontoAbertoBanner />
        <AlertaConciliacaoBanner />
        <AlertaInconsistenciasEstoqueBanner />
        <AlertaEanInvalidoBanner />
        <AlertaDivergenciaRecebimentoBanner />
        <AlertaReposicaoBanner />
        <AlertaRetomarOrcamentosBanner />
        <RodapeRecolherAlertas />
      </AlertasProvider>
      <ModalChamadoParado />
      <EmergenciaTarja />
      <EnvioDaEmergencia />
      <main className="app-content">
        {emergencia && !telaLiberadaNaEmergencia(location.pathname) && location.pathname !== telaNaQueda
          ? <TelaIndisponivelNaEmergencia />
          : children}
      </main>
      <RodapeVersao />
      <IdentificadorComponentes />
      <MockupCaptureOverlay ativo={capturaAtiva} onFechar={() => setCapturaAtiva(false)} />
      <ModalWhatsApp aberto={adminModal === 'whatsapp'} onFechar={() => setAdminModal(null)} />
      <ModalAgentes aberto={adminModal === 'agentes'} onFechar={() => setAdminModal(null)} />
      <ModalHorarios aberto={adminModal === 'horarios'} onFechar={() => setAdminModal(null)} />
      <ModalEtiquetas aberto={adminModal === 'etiquetas'} onFechar={() => setAdminModal(null)} />
      <ModalChatAtendimento aberto={adminModal === 'chat-atendimento'} onFechar={() => setAdminModal(null)} />
      <ModalCores aberto={modalCores} onFechar={() => setModalCores(false)} />
    </div>
  );
}
